import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Filters, Response } from '@ui/src';
import {
  CardsTransactions,
  CustomerType,
  StatementListItemProps,
} from '../../types';
import { customersComponentApi } from '../services/customers';

export interface CustomerStatementsState {
  cardsTransactions: {
    isLoading: boolean;
    values?: CardsTransactions[];
    cardId?: string;
    sort?: {
      sortProp: string;
      sortDir: string;
    };
    filters?: Filters;
    requestInProgress: boolean;
  };
  customerType?: CustomerType;
  selectedStatements?: string[];
  accountsTransactionsStatements?: StatementListItemProps[];
  accountId?: string;
}

const initialState: CustomerStatementsState = {
  cardsTransactions: {
    isLoading: true,
    requestInProgress: false,
    values: undefined,
  },
};

export const CustomerStatementsSlice = createSlice({
  name: 'customerStatements',
  initialState,
  reducers: {
    selectStatements: (state, action: PayloadAction<string[]>) => {
      state.selectedStatements = action.payload;
    },
    getCardsTransactions: (
      state,
      action: PayloadAction<{
        name?: string;
        direction?: 'asc' | 'desc';
        filters?: Filters;
        cardId: string;
        customerType: CustomerType;
      }>,
    ) => {
      state.cardsTransactions.cardId = action.payload.cardId;
      state.cardsTransactions.requestInProgress = true;
      state.customerType = action.payload.customerType;

      if (action.payload.name && action.payload.direction) {
        state.cardsTransactions.sort = {
          sortDir: action.payload.direction,
          sortProp: action.payload.name,
        };
      }

      if (action.payload.filters) {
        state.cardsTransactions.filters = action.payload.filters;
      }
    },
    getCardsTransactionsSuccess: (
      state,
      action: PayloadAction<CardsTransactions[]>,
    ) => {
      state.cardsTransactions.isLoading = false;
      state.cardsTransactions.values = action.payload;
      state.cardsTransactions.requestInProgress = false;
    },
    onSortCardsTransactionsList: (
      state,
      action: PayloadAction<{ name: string; direction: 'asc' | 'desc' }>,
    ) => {
      state.cardsTransactions.sort = {
        sortDir: action.payload.direction,
        sortProp: action.payload.name,
      };
      state.cardsTransactions.requestInProgress = true;
    },
    onSortCardsTransactionsListSuccess: (
      state,
      action: PayloadAction<CardsTransactions[]>,
    ) => {
      state.cardsTransactions.requestInProgress = false;
      state.cardsTransactions.values = action.payload;
    },
    onFilterCardsTransactionsList: (state, action: PayloadAction<Filters>) => {
      state.cardsTransactions.requestInProgress = true;
      state.cardsTransactions.filters = action.payload;
    },
    onFilterListCardsTransactionsSuccess: (
      state,
      action: PayloadAction<CardsTransactions[]>,
    ) => {
      state.cardsTransactions.requestInProgress = false;
      state.cardsTransactions.values = action.payload;
    },
    setAccountId: (state, action: PayloadAction<string>) => {
      state.accountId = action.payload;
    },
    clearAccountsTransactionState: (state) => {
      state.accountId = undefined;
    },
    clearCardsTransactionState: (state) => {
      state.cardsTransactions.values = undefined;
      state.cardsTransactions.cardId = undefined;
      state.cardsTransactions.isLoading = true;
      state.customerType = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      customersComponentApi.endpoints.getOverviewStatementsList.matchFulfilled,
      (state, action: PayloadAction<Response<StatementListItemProps>>) => {
        const nextStatements = action.payload.data.edges.map((item) => ({
          ...item.node,
          counterpartyAccountName: `${item.node.counterpartyFirstName || ''} ${
            item.node.counterpartyLastName || ''
          }`,
        }));

        state.accountsTransactionsStatements = nextStatements;
      },
    );
  },
});

export const {
  selectStatements,
  setAccountId,
  getCardsTransactions,
  getCardsTransactionsSuccess,
  onFilterCardsTransactionsList,
  onFilterListCardsTransactionsSuccess,
  onSortCardsTransactionsList,
  onSortCardsTransactionsListSuccess,
  clearAccountsTransactionState,
  clearCardsTransactionState,
} = CustomerStatementsSlice.actions;

export default CustomerStatementsSlice.reducer;
